//
// Copyright (c) 2020 Nutanix Inc. All rights reserved.
//
// ChangePassword - Contains the components that make up the "ChangePassword" page. This will
// the user password when the user provides the old and new password.
//
import React from 'react';
import PropTypes from 'prop-types';

import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
    StackingLayout,
    FormItemInputPassword,
    Button,
    Title,
    Alert,
    Loader,
    AlertLayout,
    Paragraph,
    Link,
    NotificationTrigger,
    NotificationItem,
} from '@nutanix-ui/prism-reactjs';
import { apiFetchRequest } from '../../actions';
import { LoginLayout } from '../../components/Common';
import { STORAGE_USERNAME } from '../../constants';
import { PATH_URL } from '../../constant';
import './ChangePassword.less';

// CHANGE TO ntnx-I18n library later
import getI18nService from '../../i18n/I18n';
import { apiRoot } from '../../config';

const i18n = getI18nService();

const OLD_PW_ID = 'oldPassword';
const NEW_PW_ID = 'newPassword';
const CONFIRM_PW_ID = 'confirmPassword';
const VALID_USER_PASSWORD =
    /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[-@$#!%^&*()_+|~=`{}[\]:";'<>,./]).{8,40}$/;
const passwordGuideURL =
    'https://portal.nutanix.com/page/documents/details?targetId=Nutanix-Security-Guide:wc-user-password-reset-wc-t.html';

const AlertPasswordRestrictions = () => {
    return (
        <Alert
            data-testid="alert-password-restrictions"
            showCloseIcon={false}
            message={
                <AlertLayout type={AlertLayout.AlertLayoutTypes.INLINE}>
                    <Paragraph>
                        {i18n.t('ChangePassword.passwordRestrictions', {
                            charNum: 8,
                            upperNum: 1,
                            lowerNum: 1,
                            numNum: 1,
                            specialNum: 1,
                        })}
                        <Link
                            type={`alert-${Alert.AlertTypes.INFO}`}
                            target="_blank"
                            href={passwordGuideURL}
                        >
                            {i18n.t('ChangePassword.learnMore')}
                        </Link>
                    </Paragraph>
                </AlertLayout>
            }
            type={Alert.AlertTypes.INFO}
        />
    );
};

export class ChangePassword extends React.Component {
    static propTypes = {
        // props for unit test
        onClickHandler: PropTypes.func,
        history: PropTypes.object,
    };

    constructor(props) {
        super(props);
        this.state = {
            // Get username from session storage when component mount
            username: this.getUsernameToSession(),
            oldPassword: '',
            newPassword: '',
            confirmPassword: '',
            errorMessage: '',
            hasInvalidPassword: false,
            isChangingPassword: false,
        };
    }

    componentDidUpdate() {
        if (this.state.errorMessage?.length) {
            this.setState({
                errorMessage: '',
            });
            NotificationTrigger.dismiss('change-password-error-notification');
            NotificationTrigger.add({
                id: 'change-password-error-notification',
                message: this.state.errorMessage,
                autoDismissDelaySecs: 0,
                type: NotificationItem.NotificationIconType.ERROR,
            });
        }
    }

    render() {
        const { oldPassword, newPassword, confirmPassword, isChangingPassword } = this.state;

        // password change Form
        const passwordForm = (
            <form id="password-change-form" onSubmit={this.handleFormSubmit}>
                <StackingLayout className="form-container" itemSpacing="20px">
                    <Title size={Title.TitleSizes.H3} tag={Title.TitleSizes.H2} role="title">
                        {i18n.t('ChangePassword.createPasswordLabel')}
                    </Title>
                    <AlertPasswordRestrictions />
                    <StackingLayout className="form-container" itemSpacing="15px">
                        <FormItemInputPassword
                            id={OLD_PW_ID}
                            data-test="changePasswordInputOldPwd"
                            value={oldPassword}
                            label={i18n.t('ChangePassword.existingPasswordLabel')}
                            type="password"
                            placeholder="Type old password"
                            onChange={this.handleOldPwInputChange}
                        />
                        <FormItemInputPassword
                            id={NEW_PW_ID}
                            data-test="changePasswordInputNewPwd"
                            value={newPassword}
                            error={!!newPassword && !this.validatePassword(newPassword)}
                            type="password"
                            label={i18n.t('ChangePassword.newPasswordLabel')}
                            placeholder={i18n.t('ChangePassword.typePasswordLabel')}
                            onChange={this.handleInputChange}
                        />
                        <FormItemInputPassword
                            id={CONFIRM_PW_ID}
                            data-test="changePasswordInputConfirmPwd"
                            value={confirmPassword}
                            error={!!confirmPassword && !this.validatePasswordToSave()}
                            label={i18n.t('ChangePassword.confirmNewPasswordLabel')}
                            placeholder={i18n.t('ChangePassword.retypePasswordLabel')}
                            onChange={this.handleInputChange}
                        />
                        <Button
                            id="changePasswordSubmit"
                            data-test="changePasswordButtonSubmit"
                            data-testid="changePasswordButtonSubmit"
                            fullWidth={true}
                            htmlType="submit"
                            aria-disabled={this.disableSubmit()}
                            className="submit-button"
                            type={
                                isChangingPassword
                                    ? Button.ButtonTypes.SECONDARY
                                    : Button.ButtonTypes.PRIMARY
                            }
                        >
                            {isChangingPassword && (
                                <Loader className="submit-loader" data-testid="submit-loader" />
                            )}
                            {isChangingPassword
                                ? i18n.t('ChangePassword.submitting')
                                : i18n.t('ChangePassword.submit')}
                        </Button>
                    </StackingLayout>
                </StackingLayout>
            </form>
        );

        return (
            <LoginLayout
                className="change-password-layout"
                form={passwordForm}
                leftTitle={i18n.t('LoginLayout.reimagineText')}
            />
        );
    }

    /**
     * Handles form submit
     * @param {Object} e - input event
     * @returns {void}
     */
    handleFormSubmit = e => {
        e.preventDefault();
        const handleClick = this.props.onClickHandler || this.handleSubmitChangePassword;
        handleClick();
    };

    /**
     * Handles input change for old password
     * @param {Object} evt - input event
     * @returns {void}
     */
    handleOldPwInputChange = evt => {
        const { value, id } = evt.target;

        // Update for oldPassword
        this.setState({
            [id]: value,
        });
    };

    /**
     * Handles input change for new / confirm password
     * Need to
     * @param {Object} evt - input event
     * @returns {void}
     */
    handleInputChange = evt => {
        const { value, id } = evt.target;
        const { newPassword, confirmPassword } = this.state;

        // Get if the new input is valid password
        const isValidPassword = this.validatePassword(value);
        // Alert is visible for NEW_PW_ID or CONFIRM_PW_ID
        // If new value as same as value of other input field
        const isSamePassword = this.confirmIsSamePassword(id, newPassword, confirmPassword, value);

        if (isSamePassword && isValidPassword) {
            // Both newPassword and confirmPassword are valid and same
            // Update state to set both alert as false
            this.setState({
                [id]: value,
                hasInvalidPassword: false,
            });
        } else {
            // Update state
            this.setState({
                [id]: value,
                hasInvalidPassword: !(isValidPassword && isSamePassword),
            });
        }
    };

    /**
   * Confirm Same as other input password
   * @param {String} id - id of new input field.
   * @param {String} newPassword - string of newPassword.
   * @param {String} confirmPassword - string of confirmPassword.
   * @param {String} newValue - new Input value
   @returns {Boolean} - current user input password
   **/
    confirmIsSamePassword = (id, newPassword, confirmPassword, newValue) => {
        if (id === NEW_PW_ID) {
            // Current input new password
            return confirmPassword === '' || newValue === confirmPassword;
        }
        // Current input is confirmPassword
        return newPassword === newValue;
    };

    /**
   * Get username from session storage
   @returns {String} - username from session storage
   **/
    getUsernameToSession = () => {
        return sessionStorage.getItem(STORAGE_USERNAME || 'username') || '';
    };

    /**
     * Disable submit button if old password / new password / confirmPassword
     * aren't valid
     * @param {String} oldPassword - string of oldPassword.
     * @param {String} newPassword - string of newPassword.
     * @param {String} confirmPassword - string of confirmPassword.
     * @param {Boolean} hasInvalidPassword - boolean of newPassword has no valid alert.
     * @returns {boolean} should disable submit button
     **/
    disableSubmit = () => {
        const {
            oldPassword,
            newPassword,
            confirmPassword,
            isChangingPassword,
            hasInvalidPassword,
        } = this.state;
        const unFilledInput = [oldPassword, newPassword, confirmPassword].some(
            input => input === ''
        );
        return unFilledInput || hasInvalidPassword || isChangingPassword;
    };

    /**
     * Handles submit for change password API request
     * @returns {void}
     */
    handleSubmitChangePassword = async () => {
        const { oldPassword, newPassword, username } = this.state;
        const { history } = this.props;

        const { valid, errorMsg } = this.validatePasswordToSave();

        if (valid) {
            const userData = {
                username,
                old_password: oldPassword,
                new_password: newPassword,
            };

            this.setState({
                isChangingPassword: true,
            });

            const response = await fetch(`${apiRoot}/users/password`, {
                method: 'put',
                body: JSON.stringify(userData),
            });

            if (response.status >= 400) {
                // Handle 400 and other errors
                const jsonResponse = await response.json();
                this.setState({
                    isChangingPassword: false,
                    errorMessage:
                        jsonResponse.message || i18n.t('ErrorMessages.unknownErrorMessage'),
                });
            } else {
                // Redirect to /login page after successful change password
                history.push({
                    pathname: PATH_URL.LOGIN,
                    state: {
                        changePWSuccess: true,
                    },
                });
            }
        } else {
            this.setState({
                isChangingPassword: false,
                errorMessage: errorMsg,
            });
        }
    };

    /**
     * Validates newPassword complexity and to be length of newPassword be at longer than 8 characters
     * @param {String} password - password
     * @returns {Boolean} - true if valid or false if invalid
     */
    validatePassword = password => {
        const regex = VALID_USER_PASSWORD;
        return regex.test(password) && password.length >= 8;
    };

    /**
     * Validates newPassword complexity, validates new password to be differente than old one
     * validates confirmPassword to be the same as newPassword and length of confirmPassword
     * be at longer than 8 characters
     * @returns {Boolean} - true if valid or false if invalid
     */
    validatePasswordToSave = () => {
        const { oldPassword, newPassword, confirmPassword } = this.state;

        // Check if new password equals old password
        if (oldPassword && oldPassword === newPassword) {
            return {
                valid: false,
                errorMsg: i18n.t('ChangePassword.differentOldPassword'),
            };
        }

        // Check if new password doesn't match confirm password
        if (newPassword !== confirmPassword) {
            return {
                valid: false,
                errorMsg: i18n.t('ChangePassword.notSamePassword'),
            };
        }

        // Check password restrictions
        const valid = this.validatePassword(newPassword);
        if (!valid) {
            const errorMsgPasswordRestrictions = i18n.t('ChangePassword.passwordRestrictions', {
                charNum: 8,
                upperNum: 1,
                lowerNum: 1,
                numNum: 1,
                specialNum: 1,
            });
            return {
                valid: false,
                errorMsg: i18n.t('ChangePassword.passwordNotValid', {
                    passwordRestrictions: errorMsgPasswordRestrictions,
                }),
            };
        }

        // All validations passed
        return { valid: true, errorMsg: '' };
    };
}

const mapStateToProps = state => {
    return {
        application: state.application,
    };
};

const enhancedChangePassword = compose(
    withRouter,
    connect(mapStateToProps, { apiFetchRequest })
)(ChangePassword);

export default enhancedChangePassword;
